import { MarkdownContent } from '@backstage/core-components';
import { makeStyles } from '@material-ui/core';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  createScaffolderFieldExtension,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';

type AlertBoxSeverity = 'error' | 'warning' | 'info' | 'success';

type AlertBoxOptions = {
  message: string;
  severity?: AlertBoxSeverity;
};

const colors = {
  error: {
    backgroundColor: 'rgb(255, 235, 238)',
    borderColor: 'rgb(239, 154, 154)',
    color: 'rgb(123, 31, 31)',
  },
  warning: {
    backgroundColor: 'rgb(255, 248, 225)',
    borderColor: 'rgb(255, 204, 128)',
    color: 'rgb(102, 60, 0)',
  },
  info: {
    backgroundColor: 'rgb(227, 242, 253)',
    borderColor: 'rgb(144, 202, 249)',
    color: 'rgb(13, 71, 161)',
  },
  success: {
    backgroundColor: 'rgb(232, 245, 233)',
    borderColor: 'rgb(165, 214, 167)',
    color: 'rgb(27, 94, 32)',
  },
};

const useStyles = makeStyles(theme => ({
  alert: {
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1, 2),
    '& > :first-child': {
      marginTop: 0,
    },
    '& > :last-child': {
      marginBottom: 0,
    },
  },
  error: {
    ...colors.error,
    border: `1px solid ${colors.error.borderColor}`,
  },
  warning: {
    ...colors.warning,
    border: `1px solid ${colors.warning.borderColor}`,
  },
  info: {
    ...colors.info,
    border: `1px solid ${colors.info.borderColor}`,
  },
  success: {
    ...colors.success,
    border: `1px solid ${colors.success.borderColor}`,
  },
}));

export function AlertBox({
  uiSchema,
}: FieldExtensionComponentProps<string | undefined, AlertBoxOptions>) {
  const classes = useStyles();
  const options = uiSchema['ui:options'];

  if (!options || typeof options.message !== 'string') {
    return null;
  }

  const severity = options.severity ?? 'error';

  return (
    <div className={`${classes.alert} ${classes[severity]}`} role="alert">
      <MarkdownContent content={options.message} />
    </div>
  );
}

export const AlertBoxFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension<string | undefined, AlertBoxOptions>({
    name: 'AlertBox',
    component: AlertBox,
    schema: {
      returnValue: {
        type: 'string',
      },
      uiOptions: {
        type: 'object',
        required: ['message'],
        properties: {
          message: {
            type: 'string',
          },
          severity: {
            type: 'string',
            enum: ['error', 'warning', 'info', 'success'],
          },
        },
      },
    },
  }),
);
